import URI from 'urijs'
import { isObject } from 'lodash'
import {
  TryIt,
  isHttpOperation,
  useParsedValue,
} from '@stoplight/elements-core'
import {
  HttpParamStyles,
  IHttpOperation,
  IHttpRequest,
  IHttpHeaderParam,
} from '@stoplight/types'

type PartialHttpRequest = Pick<IHttpRequest, 'method' | 'url'> &
  Partial<IHttpRequest> & { required?: string[] }

type HttpRequestComponentProps = { value: string }

export const HttpRequestComponent = ({ value }: HttpRequestComponentProps) => {
  const parsedValue = useParsedValue(value)

  if (
    !isObject(parsedValue) ||
    (!isPartialHttpRequest(parsedValue) && !isHttpOperation(parsedValue))
  ) {
    return null
  }

  return (
    <TryIt
      httpOperation={
        isHttpOperation(parsedValue)
          ? parsedValue
          : parseHttpRequest(parsedValue)
      }
      embeddedInMd
    />
  )
}

function isPartialHttpRequest(
  maybeHttpRequest: unknown
): maybeHttpRequest is PartialHttpRequest {
  return (
    isObject(maybeHttpRequest) &&
    'method' in maybeHttpRequest &&
    // @ts-ignore
    typeof maybeHttpRequest['method'] === 'string' &&
    'url' in maybeHttpRequest &&
    // @ts-ignore
    typeof maybeHttpRequest['url'] === 'string'
  )
}

function parseHttpRequest(data: PartialHttpRequest): IHttpOperation {
  const uri = URI(data.url)
  const pathParam = data.url.match(/[^{\\}]+(?=})/g)

  const headers: IHttpHeaderParam[] = Object.entries(data.headers || {}).map(
    ([key, value]) => ({
      id: `?http-header-${key}-id?`,
      name: key,
      style: HttpParamStyles.Simple,
      schema: { default: value },
    })
  )

  return {
    id: '?http-operation-id?',
    method: data.method,
    path: uri.is('absolute') ? uri.path() : data.url,
    servers: [
      {
        id: `?http-server-${uri.href()}?`,
        url: uri.is('absolute') ? uri.origin() : data.baseUrl || '',
      },
    ],
    request: {
      query: Object.entries(data.query || {}).map(([key, value]) => ({
        id: `?http-query-${key}-id?`,
        name: key,
        style: HttpParamStyles.Form,
        schema: {
          default: Array.isArray(value) && value.length > 0 ? value[0] : value,
        },
      })),
      headers: headers.filter((h) => !h.schema?.default),
      path: pathParam?.map((name) => ({
        id: `?http-param-${name}-id?`,
        name,
        style: HttpParamStyles.Simple,
        required: true,
      })),
      ...(data.body
        ? {
            body: {
              id: '?http-request-body?',
              contents: [
                {
                  id: '?http-request-body-media?',
                  mediaType:
                    headers
                      .find((h) => h.name === 'Content-Type')
                      ?.schema?.default?.toString() || 'application/json',
                  schema: {
                    properties: Object.entries(data.body || {}).reduce(
                      (acc, [key, value]) => ({
                        ...acc,
                        [key]: {
                          type: typeof value,
                          examples: [value],
                        },
                      }),
                      {}
                    ),
                    required: data.required,
                  },
                },
              ],
            },
          }
        : null),
    },
    responses: [],
  }
}
